import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { matchPath, NavLink as RouterLink, useLocation } from 'react-router-dom';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Link, Grid, Stack, ListItem, Tooltip, Paper, Divider } from '@mui/material';
import { tooltipClasses } from '@mui/material/Tooltip';
// components
import Iconify from '../../components/Iconify';
import { useAppContext } from '../../contexts/AppContext';
import { APP_STATE } from '../../constants/app_state';
import { Icon } from '@iconify/react';

// ----------------------------------------------------------------------

const LinkStyle = styled(Link)(({ theme }) => ({
  ...theme.typography.subtitle2,
  color: theme.palette.text.primary,
  fontWeight: 'bolder',
  transition: theme.transitions.create('opacity', {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    opacity: 0.48,
    textDecoration: 'none',
  },
}));

const ListItemStyle = styled(ListItem)(({ theme }) => ({
  ...theme.typography.body2,
  fontWeight: 600,
  padding: 0,
  marginTop: theme.spacing(2),
  color: theme.palette.text.secondary,
  transition: theme.transitions.create('color'),
}));

const HtmlTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)(
  ({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      padding: 0,
      borderRadius: 0,
      backgroundColor: theme.palette.background.paper,
      color: 'rgba(0, 0, 0, 0.87)',
      minWidth: '100vw',
      fontSize: theme.typography.pxToRem(12),
      border: 'none',
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: '#FFF',
    },
  })
);

// ----------------------------------------------------------------------

MenuDesktop.propTypes = {
  isHome: PropTypes.bool,
  isOffset: PropTypes.bool,
  navConfig: PropTypes.array,
};

export default function MenuDesktop({ isOffset, isHome, navConfig, secondaryNav }) {
  const { pathname } = useLocation();

  return (
    <Stack>
      {!isOffset && (
        <Stack width={1} direction="row" flexWrap="wrap" spacing={2}>
          {secondaryNav.map((link) => (
            <SecondMenuDesktopItem
              key={link.title}
              item={link}
              isOffset={isOffset}
              isHome={isHome}
              pathname={pathname}
            />
          ))}
        </Stack>
      )}

      {!isOffset && <Divider sx={{ pt: 0.5 }} />}

      <Stack direction="row" flexWrap="wrap" spacing={2}>
        {navConfig.map((link) => (
          <MenuDesktopItem key={link.title} item={link} isOffset={isOffset} isHome={isHome} pathname={pathname} />
        ))}
      </Stack>
    </Stack>
  );
}

// ----------------------------------------------------------------------

IconBullet.propTypes = {
  type: PropTypes.oneOf(['item', 'subheader']),
};

function IconBullet({ type = 'item' }) {
  return (
    <Box sx={{ width: 24, height: 16, display: 'flex', alignItems: 'center' }}>
      <Box
        component="span"
        sx={{
          ml: '2px',
          width: 4,
          height: 4,
          borderRadius: '50%',
          bgcolor: 'currentColor',
          ...(type !== 'item' && { ml: 0, width: 8, height: 2, borderRadius: 2 }),
        }}
      />
    </Box>
  );
}

// ----------------------------------------------------------------------

MenuDesktopItem.propTypes = {
  isOffset: PropTypes.bool,
  pathname: PropTypes.string,
  item: PropTypes.shape({
    path: PropTypes.string,
    title: PropTypes.string,
    children: PropTypes.array,
  }),
};

function MenuDesktopItem({ item, pathname }) {
  const { title, path, children, disable, aside } = item;
  const { appdispatch } = useAppContext();

  useEffect(() => {
    if (open) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const match = !!matchPath({ path, end: true }, pathname);

  const handleSelected = () => {
    appdispatch({
      type: APP_STATE.subMenu,
      payload: children?.at(0)?.items || [],
    });
  };

  if (children) {
    return (
      <>
        <HtmlTooltip
          arrow
          title={
            <Paper
              sx={{
                position: 'relative',
                borderRadius: 0,
                bgcolor: (t) => t.palette.background.paper,
                maxWidth: '100%',
                minHeight: 300,
                boxShadow: (theme) => theme.customShadows.z24,
              }}
            >
              <Stack direction="row" width={1} alignItems="center">
                <Stack width={0.65} p={3} pl={{ lg: 5 }}>
                  <Box
                    component="img"
                    position="absolute"
                    bottom={0}
                    left={-25}
                    src="/assets/background/icon_logo_line_to_right.png"
                    width={150}
                    sx={{ transform: 'rotateY(180deg)' }}
                  />

                  <Stack direction="row" position="absolute" top={10} bottom={10} height={0.9} right={50} width={150}>
                    {aside && <Box component="img" src={aside || ''} height={1} width={1} sx={{ opacity: 0.1 }} />}
                  </Stack>

                  {children.map((list) => {
                    const { subheader, items } = list;

                    return (
                      <Grid key={subheader} container sx={{ px: 5, zIndex: 20 }} spacing={1}>
                        {items.map((item) => (
                          <Grid item md={6} key={item.title}>
                            <Stack
                              component={item?.external ? 'a' : RouterLink}
                              {...(item?.external && { href: item.path, target: '_blank' })}
                              to={item.path}
                              onClick={handleSelected}
                              direction="row"
                              spacing={3}
                              alignItems="center"
                              sx={{ textDecoration: 'none', cursor: 'pointer' }}
                            >
                              {item?.icon || (
                                <Icon icon={item?.icon || 'mdi:location-radius'} height={50} width={50} color="black" />
                              )}
                              <Stack spacing={1}>
                                <ListItemStyle
                                  sx={{
                                    color: disable ? 'red' : 'black',
                                    '&.active': {
                                      typography: 'subtitle2',
                                      fontWeight: 'bold',
                                      color: '#f9c011',
                                    },
                                    '&:hover': {
                                      opacity: 0.7,
                                      textDecoration: 'none',
                                    },
                                  }}
                                >
                                  {item.title}
                                </ListItemStyle>
                                <Box height={5} width={30} bgcolor="primary.main" />
                              </Stack>
                            </Stack>
                          </Grid>
                        ))}
                      </Grid>
                    );
                  })}
                </Stack>
                {/* <Stack width={0.35}>
                  <Box
                    component="img"
                    src={children?.at(0)?.aside || '/assets/background/AdobeStock_144613163.jpg'}
                    height={400}
                    width="100%"
                  />
                </Stack> */}
              </Stack>
            </Paper>
          }
        >
          <Stack position="relative" pb={0.4} pt={2} onMouseEnter={handleOpen} onMouseLeave={handleClose}>
            <LinkStyle
              onClick={handleOpen}
              {...(path === '/' && { component: RouterLink, to: path })}
              sx={{
                display: 'flex',
                cursor: 'pointer',
                alignItems: 'center',
                // color: 'common.white',
                fontSize: { md: 11, lg: 12, xl: 13 },
                ...(open && { opacity: 0.48 }),
              }}
            >
              {title}
              <Iconify
                icon={open ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'}
                sx={{ ml: 0.5, width: 16, height: 16 }}
              />
            </LinkStyle>
          </Stack>
        </HtmlTooltip>
      </>
    );
  }

  return (
    <Stack position="relative" pb={0.4} pt={2}>
      <LinkStyle
        to={path}
        component={RouterLink}
        onClick={handleSelected}
        end={path === '/'}
        sx={{
          fontSize: { md: 11, lg: 12, xl: 13 },
        }}
      >
        {title}
      </LinkStyle>
      {match && <Box width={1} borderRadius={1} position="absolute" bottom={0} bgcolor="#f9c011" height={5} />}
    </Stack>
  );
}

function SecondMenuDesktopItem({ item, pathname }) {
  const { title, path, children, bgcolor, color } = item;
  const { appdispatch } = useAppContext();

  useEffect(() => {
    if (open) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const match = !!matchPath({ path, end: true }, pathname);

  const handleSelected = () => {
    handleOpen();
    appdispatch({
      type: APP_STATE.subMenu,
      payload: children?.at(0)?.items || [],
    });
  };

  return (
    <Stack position="relative" pb={0.4} pt={1}>
      <LinkStyle
        to={path}
        component={RouterLink}
        onClick={handleSelected}
        end={path === '/'}
        sx={{
          fontSize: { md: 10 },
          ...(bgcolor && { color, bgcolor, px: 1, py: 0.3, borderRadius: 0.5 }),
          opacity: 0.68,
          '&:hover': {
            textDecoration: 'none',
          },
        }}
      >
        {title}
      </LinkStyle>
      {match && <Box width={1} borderRadius={1} position="absolute" bottom={0} bgcolor="#f9c011" height={5} />}
    </Stack>
  );
}
